import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import { CheckCircle2, ExternalLink, Inbox, Mail, ShieldCheck } from "lucide-react";
import { CaptchaField } from "@/components/auth/CaptchaField";
import { SiteLink } from "@/components/SiteLink";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { LandingNav } from "@/components/landing/LandingNav";
import { Container, ContourField, Eyebrow } from "@/components/landing/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/hooks/useLanguage";
import { currentCaptchaMode, type CaptchaMode } from "@/lib/captcha";
import { isLandingLocale, makeTr } from "@/lib/landing/locale";
import { OPERATOR, SUPPORT_EMAIL } from "@/lib/seo/productFacts";

/**
 * The contact page: the form (stored, then e-mailed to support by the
 * contact-form function), the support address, and the operator's details.
 *
 * Prerendered like the landing page: the language comes from the URL, and
 * nothing reads window during render (the CAPTCHA mode is set after mount).
 */

type Status = "idle" | "loading" | "success" | "error";

const ERROR_CODES = [
  "INVALID_EMAIL",
  "INVALID_MESSAGE",
  "CAPTCHA_FAILED",
  "RATE_LIMITED",
  "SAVE_FAILED",
] as const;
type ErrorCode = (typeof ERROR_CODES)[number] | "NETWORK" | "CONFIG";

export default function Contact() {
  const { lang } = useParams<{ lang: string }>();
  // LanguageRoute redirects an unknown prefix before this renders.
  const locale = isLandingLocale(lang) ? lang : "cs";
  const tr = makeTr(locale);
  const { language, setLanguage } = useLanguage();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorCode, setErrorCode] = useState<ErrorCode | null>(null);
  const [sentTo, setSentTo] = useState("");

  const [captchaMode, setCaptchaMode] = useState<CaptchaMode>({ kind: "off" });
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const widgetRef = useRef<TurnstileInstance>(null);

  useEffect(() => {
    setCaptchaMode(currentCaptchaMode());
  }, []);

  // Keep the app's language (CAPTCHA copy, later pages) in step with the URL.
  useEffect(() => {
    if (locale !== language) setLanguage(locale);
  }, [locale, language, setLanguage]);

  const errorText: Record<ErrorCode, string> = {
    INVALID_EMAIL: tr("Zkontrolujte e-mailovou adresu.", "Check the e-mail address."),
    INVALID_MESSAGE: tr(
      "Zpráva musí mít alespoň 10 znaků.",
      "The message needs at least 10 characters."
    ),
    CAPTCHA_FAILED: tr(
      "Bezpečnostní kontrola neprošla. Zkuste to prosím znovu.",
      "The security check failed. Please try again."
    ),
    RATE_LIMITED: tr(
      "Zpráv bylo odesláno příliš mnoho. Zkuste to za hodinu, nebo napište přímo na e-mail.",
      "Too many messages were sent. Try again in an hour, or e-mail us directly."
    ),
    SAVE_FAILED: tr(
      "Zprávu se nepodařilo odeslat. Zkuste to znovu, nebo napište přímo na e-mail.",
      "The message couldn't be sent. Try again, or e-mail us directly."
    ),
    NETWORK: tr(
      "Zprávu se nepodařilo odeslat. Zkontrolujte připojení, nebo napište přímo na e-mail.",
      "The message couldn't be sent. Check your connection, or e-mail us directly."
    ),
    CONFIG: tr(
      "Formulář teď nefunguje. Napište nám prosím přímo na e-mail.",
      "The form isn't working right now. Please e-mail us directly."
    ),
  };

  const captchaPending = captchaMode.kind === "widget" && !captchaToken;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
    if (!supabaseUrl || !anonKey) {
      setErrorCode("CONFIG");
      setStatus("error");
      return;
    }

    setStatus("loading");
    setErrorCode(null);

    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/contact-form`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: anonKey,
          Authorization: `Bearer ${anonKey}`,
        },
        body: JSON.stringify({
          email,
          name,
          subject,
          message,
          language: locale,
          website: honeypot,
          captcha_token: captchaToken ?? undefined,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { code?: string };

      if (!res.ok) {
        const code = ERROR_CODES.find((c) => c === data.code) ?? "SAVE_FAILED";
        setErrorCode(code);
        setStatus("error");
        return;
      }

      setSentTo(email);
      setStatus("success");
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
    } catch {
      setErrorCode("NETWORK");
      setStatus("error");
    } finally {
      // Turnstile tokens are single-use, whatever the outcome.
      setCaptchaToken(null);
      widgetRef.current?.reset();
    }
  }

  return (
    <div
      lang={locale}
      className="min-h-screen overflow-x-clip bg-background text-foreground antialiased"
    >
      <LandingNav locale={locale} path="/contact" />

      <main id="obsah">
        <section aria-labelledby="contact-title" className="relative isolate overflow-hidden">
          <ContourField variant="hero" className="opacity-[0.14] dark:opacity-[0.08]" />
          <Container className="grid gap-12 pb-20 pt-14 sm:pt-20 lg:grid-cols-[1fr_1.15fr] lg:gap-16 lg:pb-28">
            {/* Who to write to */}
            <div>
              <Eyebrow className="mb-3">{tr("Kontakt", "Contact")}</Eyebrow>
              <h1
                id="contact-title"
                className="text-[2rem] font-semibold leading-[1.1] tracking-tight sm:text-5xl"
              >
                {tr("Napište nám", "Write to us")}
              </h1>
              <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg">
                {tr(
                  "Dotaz k Zettlu, k účtu nebo k předplatnému? Odpovídáme do jednoho pracovního dne.",
                  "A question about Zettl, your account or your subscription? We reply within one business day."
                )}
              </p>

              <ul className="mt-10 space-y-3">
                <ContactRow
                  icon={<Mail className="h-4 w-4" />}
                  title={tr("Podpora", "Support")}
                  detail={tr(
                    "Dotazy k aplikaci, účtu a fakturaci",
                    "The app, your account and billing"
                  )}
                >
                  <a href={`mailto:${SUPPORT_EMAIL}`} className={linkClass}>
                    {SUPPORT_EMAIL}
                  </a>
                </ContactRow>
                <ContactRow
                  icon={<ShieldCheck className="h-4 w-4" />}
                  title={tr("Osobní údaje a právní věci", "Personal data and legal matters")}
                  detail={tr(
                    "Žádosti podle GDPR, smlouvy, reklamace",
                    "GDPR requests, contracts, complaints"
                  )}
                >
                  <a href={`mailto:${OPERATOR.email}`} className={linkClass}>
                    {OPERATOR.email}
                  </a>
                </ContactRow>
                <ContactRow
                  icon={<Inbox className="h-4 w-4" />}
                  title={tr("Datová schránka", "Data box")}
                  detail={tr("Pro úřední korespondenci", "For official correspondence")}
                >
                  <span className="font-mono">xqbbztu</span>
                </ContactRow>
              </ul>
            </div>

            {/* The form */}
            <div className="rounded-xl border bg-card p-5 shadow-sm sm:p-7">
              {status === "success" ? (
                <div role="status" className="flex flex-col items-start gap-3 py-6">
                  <CheckCircle2
                    aria-hidden="true"
                    className="h-8 w-8 text-[hsl(var(--action))] dark:text-primary"
                  />
                  <h2 className="text-xl font-semibold tracking-tight">
                    {tr("Děkujeme, zprávu máme.", "Thank you, we have your message.")}
                  </h2>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {sentTo
                      ? tr(
                          `Odpovíme do jednoho pracovního dne na ${sentTo}.`,
                          `We'll reply to ${sentTo} within one business day.`
                        )
                      : tr(
                          "Odpovíme do jednoho pracovního dne.",
                          "We'll reply within one business day."
                        )}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-2"
                    onClick={() => setStatus("idle")}
                  >
                    {tr("Napsat další zprávu", "Write another message")}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <h2 className="text-lg font-semibold tracking-tight">
                    {tr("Zpráva", "Message")}
                  </h2>
                  {/* Bots fill this; people never see it. */}
                  <div className="sr-only" aria-hidden="true">
                    <Label htmlFor="contact-website">Website</Label>
                    <Input
                      id="contact-website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field
                      id="contact-name"
                      label={tr("Jméno", "Name")}
                      optional={tr("nepovinné", "optional")}
                    >
                      <Input
                        id="contact-name"
                        name="name"
                        autoComplete="name"
                        maxLength={120}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </Field>
                    <Field id="contact-email" label={tr("E-mail pro odpověď", "Reply-to e-mail")}>
                      <Input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        maxLength={254}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </Field>
                  </div>
                  <Field
                    id="contact-subject"
                    label={tr("Předmět", "Subject")}
                    optional={tr("nepovinné", "optional")}
                  >
                    <Input
                      id="contact-subject"
                      name="subject"
                      maxLength={200}
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </Field>
                  <Field id="contact-message" label={tr("Zpráva", "Message")}>
                    <Textarea
                      id="contact-message"
                      name="message"
                      required
                      minLength={10}
                      maxLength={8000}
                      rows={6}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="min-h-[148px] resize-y"
                    />
                  </Field>

                  <CaptchaField
                    mode={captchaMode}
                    widgetRef={widgetRef}
                    onToken={setCaptchaToken}
                  />

                  {status === "error" && errorCode ? (
                    <p role="alert" className="text-sm text-destructive">
                      {errorText[errorCode]}{" "}
                      <a href={`mailto:${SUPPORT_EMAIL}`} className={linkClass}>
                        {SUPPORT_EMAIL}
                      </a>
                    </p>
                  ) : null}

                  <div className="flex flex-col-reverse gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      {tr("Odesláním berete na vědomí ", "By sending, you acknowledge our ")}
                      <SiteLink to="/privacy" className={linkClass}>
                        {tr("zásady ochrany osobních údajů", "privacy policy")}
                      </SiteLink>
                      .
                    </p>
                    <Button
                      type="submit"
                      className="h-11 shrink-0 px-5"
                      disabled={status === "loading" || captchaPending}
                    >
                      {status === "loading"
                        ? tr("Odesílám…", "Sending…")
                        : tr("Odeslat zprávu", "Send message")}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </Container>
        </section>

        {/* The operator and the authorities: required, quiet. */}
        <section aria-labelledby="operator-title" className="border-t border-border/60">
          <Container className="grid gap-10 py-14 text-sm sm:py-16 md:grid-cols-3">
            <div>
              <h2 id="operator-title" className="text-xs font-medium text-muted-foreground">
                {tr("Provozovatel", "Operator")}
              </h2>
              <p className="mt-3 font-medium">{OPERATOR.legalName}</p>
              <p className="mt-1 leading-relaxed text-muted-foreground">
                {OPERATOR.address.streetAddress}, Stodůlky
                <br />
                {OPERATOR.address.postalCode} Praha 5, {tr("Česká republika", "Czech Republic")}
              </p>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                {tr("IČO", "Company ID")} {OPERATOR.ico} · {tr("neplátce DPH", "not a VAT payer")}
                <br />
                {tr("Základní kapitál", "Share capital")} 20 000 Kč
              </p>
            </div>
            <Authority
              title={tr("Úřad pro ochranu osobních údajů", "Office for Personal Data Protection")}
              detail={tr(
                "Stížnosti na zpracování osobních údajů",
                "Complaints about personal data processing"
              )}
              href="https://www.uoou.cz"
              label="www.uoou.cz"
            />
            <Authority
              title={tr("Česká obchodní inspekce", "Czech Trade Inspection Authority")}
              detail={tr(
                "Mimosoudní řešení spotřebitelských sporů",
                "Out-of-court resolution of consumer disputes"
              )}
              href="https://www.coi.cz"
              label="www.coi.cz"
            />
          </Container>
        </section>
      </main>

      <LandingFooter locale={locale} path="/contact" />
    </div>
  );
}

const linkClass =
  "font-medium text-[hsl(var(--action))] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--action))] rounded-sm dark:text-primary dark:focus-visible:ring-primary";

function ContactRow({
  icon,
  title,
  detail,
  children,
}: {
  icon: ReactNode;
  title: string;
  detail: string;
  children: ReactNode;
}) {
  return (
    <li className="flex gap-4 rounded-xl border bg-card/60 p-4">
      <span
        aria-hidden="true"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-[hsl(var(--action))] dark:text-primary"
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{detail}</p>
        <p className="mt-1.5 break-words text-[15px]">{children}</p>
      </div>
    </li>
  );
}

function Field({
  id,
  label,
  optional,
  children,
}: {
  id: string;
  label: string;
  optional?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label}
        {optional && <span className="ml-1.5 font-normal text-muted-foreground">({optional})</span>}
      </Label>
      {children}
    </div>
  );
}

function Authority({
  title,
  detail,
  href,
  label,
}: {
  title: string;
  detail: string;
  href: string;
  label: string;
}) {
  return (
    <div>
      <h3 className="font-medium">{title}</h3>
      <p className="mt-1 leading-relaxed text-muted-foreground">{detail}</p>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`mt-2 inline-flex items-center gap-1 ${linkClass}`}
      >
        {label}
        <ExternalLink aria-hidden="true" className="h-3 w-3" />
      </a>
    </div>
  );
}
